<template>
  <div class="p-4" data-tour="home-block-library">
    <section
      v-for="group in groups"
      :key="group.name"
      :aria-labelledby="`block-group-${group.id}`"
      class="mb-6 last:mb-0"
    >
      <h3
        :id="`block-group-${group.id}`"
        class="text-xs font-semibold tracking-wide text-gray-500 uppercase"
      >
        {{ group.name }}
      </h3>
      <ul class="mt-2 space-y-2">
        <li
          v-for="entry in group.blocks"
          :key="entry.kind"
          class="flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors"
          :class="
            entry.onPage
              ? 'border-gray-100 bg-gray-50'
              : 'border-gray-200 bg-white hover:border-purple-300'
          "
        >
          <span
            aria-hidden="true"
            class="flex size-9 shrink-0 items-center justify-center rounded-lg"
            :class="
              entry.onPage
                ? 'bg-gray-100 text-gray-400'
                : 'bg-purple-50 text-purple-700'
            "
          >
            <Icon :name="entry.icon" class="size-4.5" />
          </span>
          <div class="min-w-0 flex-1">
            <p
              class="truncate text-sm font-semibold"
              :class="entry.onPage ? 'text-gray-500' : 'text-gray-900'"
            >
              {{ entry.label }}
            </p>
            <p class="mt-0.5 line-clamp-2 text-xs leading-5 text-gray-500">
              {{ entry.description }}
            </p>
          </div>
          <span
            v-if="entry.onPage"
            class="inline-flex shrink-0 items-center gap-1 self-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500"
          >
            <Icon name="check" class="size-3" />
            On page
          </span>
          <button
            v-else
            type="button"
            class="btn-basic shrink-0 self-center px-2.5! py-1! text-xs hover:bg-purple-100"
            :aria-label="`Add ${entry.label} block`"
            @click="addHandler?.(entry.kind)"
          >
            <Icon name="plus" class="size-3" />
            Add
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Icon from "@/components/Core/Icon.vue";
import { useHomeBlockLibrary } from "@/composables/useHomeBlockLibrary";
import { HomepageBlockKind } from "@/services/homepage";
import { BLOCK_REGISTRY, BlockGroup } from "@/views/Home/blocks";
import { useHasManagerCapability } from "@/services/user";

const { onPageKinds, addHandler } = useHomeBlockLibrary();
// Backend-authorized manager: these controls guard IsManager-gated writes,
// and IsManager grants superusers before checking the org role.
const isManager = useHasManagerCapability();

const GROUP_ORDER: BlockGroup[] = [
  "Act on today",
  "Improve",
  "Explore",
  "Product",
];

const groups = computed(() =>
  GROUP_ORDER.map((name) => ({
    name,
    id: name.toLowerCase().replace(/\s+/g, "-"),
    blocks: Object.entries(BLOCK_REGISTRY)
      .filter(
        ([, definition]) =>
          definition.group === name &&
          (!definition.managerOnly || isManager.value),
      )
      .map(([kind, definition]) => ({
        kind: kind as HomepageBlockKind,
        label: definition.label,
        description: definition.description,
        icon: definition.icon,
        onPage: onPageKinds.value.includes(kind as HomepageBlockKind),
      })),
  })).filter((group) => group.blocks.length > 0),
);
</script>
